import {convertImageToWebP} from '@/helpers/shadcn/utils'
import {deleteProductMediaService} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {FILE, SUPPORTED_PRODUCT_MEDIA} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {move} from '@dnd-kit/helpers'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useCallback, useMemo, useState} from 'react'
import {generateVideoThumbnail} from '@/helpers/mediaThumbnail'

const initialFileErrorState = {feedbackType: 'info', helperText: ''}
const supportedProductMediaDescription = SUPPORTED_PRODUCT_MEDIA.join(', ')
const MEDIA_SLOT_RULES = {
  1: {
    allowedMediaKinds: ['IMAGE'],
    helperText: 'Slot 1 only accepts JPG, PNG, WEBP image files.',
  },
  2: {
    allowedMediaKinds: ['IMAGE', 'GIF', 'MP4'],
    helperText: 'Slot 2 accepts image, GIF or MP4 files.',
  },
  3: {
    allowedMediaKinds:  ['IMAGE', 'GIF', 'MP4'],
    helperText: 'Slot 3 accepts image, GIF or MP4 files.',
  },
}

const resolveMediaKind = file => {
  const fileType = (file?.type || '').toLowerCase()

  if (fileType === 'image/gif') return 'GIF'
  if (fileType === 'video/mp4') return 'MP4'
  if (fileType.startsWith('image/')) return 'IMAGE'

  return 'UNSUPPORTED'
}

const getMediaTitle = mediaKind => {
  if (mediaKind === 'MP4') return 'mp4'
  if (mediaKind === 'GIF') return 'gif'
  return 'image'
}

const resolveSlotMediaKind = slot => {
  const fileType = slot?.file?.type?.toLowerCase()
  const mediaType = slot?.metadata?.mediaType
  const mediaTitle = slot?.metadata?.mediaTitle?.toLowerCase()

  if (mediaType === 'VIDEO' || fileType === 'video/mp4') return 'MP4'
  if (mediaTitle === 'gif' || fileType === 'image/gif') return 'GIF'
  if (slot?.file || slot?.imageUri || slot?.metadata) return 'IMAGE'

  return 'EMPTY'
}

const remapByDisplayOrder = (record, displayOrderMap) => {
  const next = {}

  Object.entries(record || {}).forEach(([displayOrder, value]) => {
    next[displayOrderMap.get(Number(displayOrder)) ?? Number(displayOrder)] = value
  })

  return next
}

const remapMediaForDisplayOrder = (media, displayOrder) => ({
  ...media,
  metadata: media?.metadata
    ? {
        ...media.metadata,
        displayOrder,
      }
    : media?.metadata,
})

export const useMediaGallery = ({productId, mediaSlots, updateMediaSlot, clearMediaSlot}) => {
  const dispatch = useAppDispatch()
  const {maxIndividualImageSize = '2MB'} = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}
  const [mediaSlotUiState, setMediaSlotUiState] = useState({})
  const [isDeletingFromServer, setIsDeletingFromServer] = useState(false)

  const updateMediaSlotUiState = useCallback((displayOrder, updates) => {
    setMediaSlotUiState(prev => ({
      ...prev,
      [displayOrder]: {
        uploading: false,
        error: initialFileErrorState,
        ...prev[displayOrder],
        ...updates,
      },
    }))
  }, [])

  const galleryMediaSlots = useMemo(
    () =>
      mediaSlots.map(slot => {
        const slotUiState = mediaSlotUiState[slot.displayOrder] || {}
        return {
          ...slot,
          uploading: slotUiState.uploading || false,
          error: slotUiState.error || initialFileErrorState,
        }
      }),
    [mediaSlots, mediaSlotUiState]
  )

  const hasImageInFirstSlot = useCallback(() => {
    const firstSlot = mediaSlots.find(slot => slot.displayOrder === 1)
    return Boolean(firstSlot?.file || firstSlot?.imageUri)
  }, [mediaSlots])

  const hasGifOrVideoInLaterSlots = useCallback(() => {
    return mediaSlots.some(slot => {
      if (slot.displayOrder === 1) return false

      const mediaType = slot?.metadata?.mediaType
      const mediaTitle = slot?.metadata?.mediaTitle?.toLowerCase()
      const fileType = slot?.file?.type?.toLowerCase()

      return mediaType === 'VIDEO' || mediaTitle === 'gif' || fileType === 'video/mp4' || fileType === 'image/gif'
    })
  }, [mediaSlots])

  const handleUpload = useCallback(
    async (displayOrder, file) => {
      if (!file) return
      const mediaKind = resolveMediaKind(file)
      const slotRule = MEDIA_SLOT_RULES[displayOrder]

      if (!slotRule || !slotRule.allowedMediaKinds?.includes(mediaKind)) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: slotRule?.helperText || `Invalid file format. Please use ${supportedProductMediaDescription} files only.`,
          },
        })
        return
      }

      const maxSizeMB = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) || 2
      if ((mediaKind === 'IMAGE' || mediaKind === 'GIF') && file.size > maxSizeMB * FILE.SIZE_BYTES) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: `Images and GIFs must be less than or equal to ${maxSizeMB} MB.`,
          },
        })
        return
      }

      if ((mediaKind === 'MP4' || mediaKind === 'GIF') && displayOrder !== 1 && !hasImageInFirstSlot()) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: 'You must upload an image in slot 1 before adding a video or GIF in slots 2 or 3.',
          },
        })
        return
      }

      if (mediaKind === 'MP4' && file.size > 100 * FILE.SIZE_BYTES) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: 'Videos must be less than or equal to 100 MB.',
          },
        })
        return
      }

      try {
        updateMediaSlotUiState(displayOrder, {uploading: true, error: initialFileErrorState})

        let fileURL = URL.createObjectURL(file)

        if (mediaKind === 'MP4') {
          try {
            fileURL = await generateVideoThumbnail(file, {
              maxWidth: 468,
              maxHeight: 300,
              outputType: 'image/jpeg',
              quality: 0.85,
              captureAtSecond: 1,
            })
          } catch {
            // Keep object URL fallback when MP4 thumbnail generation fails.
          }
        }

        if (mediaKind !== 'IMAGE') {
          updateMediaSlot(displayOrder, {
            file,
            imageUri: fileURL,
            metadata: {
              id: productId,
              displayOrder,
              mediaType: mediaKind === 'MP4' ? 'VIDEO' : 'IMAGE',
              mediaTitle: getMediaTitle(mediaKind),
              originalFileName: file.name,
              referenceEntity: 'product',
            },
          })

          return
        }

        const convertedFiles = await convertImageToWebP(file, {
          entityType: 'product',
        })

        if (convertedFiles?.metadata && convertedFiles?.webpFile) {
          const mediaMetadata = {
            ...convertedFiles.metadata,
            id: productId,
            displayOrder,
            mediaType: 'IMAGE',
            mediaTitle: getMediaTitle(mediaKind),
            referenceEntity: 'product',
          }

          updateMediaSlot(displayOrder, {
            file: convertedFiles.webpFile,
            imageUri: fileURL,
            metadata: mediaMetadata,
          })
        }
      } catch (error) {
        updateMediaSlotUiState(displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: `Failed to process file. Supported formats: ${supportedProductMediaDescription}`,
          },
        })
      } finally {
        updateMediaSlotUiState(displayOrder, {uploading: false})
      }
    },
    [hasImageInFirstSlot, productId, maxIndividualImageSize, updateMediaSlot, updateMediaSlotUiState]
  )

  const handleUploadError = useCallback(
    (displayOrder, error) => {
      updateMediaSlotUiState(displayOrder, {
        error: {
          feedbackType: 'error',
          helperText: error || `Invalid file format. Please use ${supportedProductMediaDescription} files only.`,
        },
      })
    },
    [updateMediaSlotUiState]
  )

  const handleDeleteImage = useCallback(
    displayOrder => {
      if (displayOrder === 1 && mediaSlots.find(slot => slot.displayOrder === 1)?.file && hasGifOrVideoInLaterSlots()) {
        const restrictionError = new Error('You cannot delete the image in slot 1.')  
 dispatch(
          setToastNotifications({
            show: true,
            message: `You cannot delete the image in slot 1.`,
            type: 'error',
          })
        )
        throw restrictionError
      }

      clearMediaSlot(displayOrder)
      updateMediaSlotUiState(displayOrder, {error: initialFileErrorState})
    },
    [clearMediaSlot, hasGifOrVideoInLaterSlots, updateMediaSlotUiState]
  )

  const handleDeleteFromServer = useCallback(
    async displayOrder => {
      try {
        setIsDeletingFromServer(true)
        await deleteProductMediaService({productId, displayOrder})

        dispatch(
          setToastNotifications({
            show: true,
            message: `Image deleted successfully`,
            type: 'success',
          })
        )

        handleDeleteImage(displayOrder)
      } catch (error) {
        dispatch(
          setToastNotifications({
            show: true,
            message: error?.message || 'Failed to delete image',
            type: 'error',
          })
        )
      } finally {
        setIsDeletingFromServer(false)
      }
    },
    [productId, dispatch, handleDeleteImage]
  )

  const handleReorder = useCallback(
    event => {
      const reordered = move(
        mediaSlots.map(slot => ({
          id: slot.slotId || `slot-${slot.displayOrder}`,
          displayOrder: slot.displayOrder,
        })),
        event
      )

      const displayOrderMap = new Map(reordered.map((slot, index) => [slot.displayOrder, index + 1]))
      const hasMoved = [...displayOrderMap].some(([oldOrder, newOrder]) => oldOrder !== newOrder)

      if (!hasMoved) {
        updateMediaSlotUiState(1, {
          error: {
            feedbackType: null,
            helperText: '',
          },
        })
        return
      }

      const mediaByDisplayOrder = new Map(
        mediaSlots.map(slot => [
          slot.displayOrder,
          {
            file: slot.file,
            imageUri: slot.imageUri,
            metadata: slot.metadata,
          },
        ])
      )

      const reorderedSlots = [...displayOrderMap.entries()].map(([oldOrder, newOrder]) => {
        const media = mediaByDisplayOrder.get(oldOrder) || {file: null, imageUri: '', metadata: null}

        return {
          displayOrder: newOrder,
          mediaKind: resolveSlotMediaKind(media),
        }
      })

      const invalidSlot = reorderedSlots.find(slot => {
        if (slot.mediaKind === 'EMPTY') return false

        const slotRule = MEDIA_SLOT_RULES[slot.displayOrder]
        return !slotRule?.allowedMediaKinds?.includes(slot.mediaKind)
      })

      if (invalidSlot) {
        const slotRule = MEDIA_SLOT_RULES[invalidSlot.displayOrder]
        const errorMessage = slotRule?.helperText || 'Invalid media type for this slot.'

        updateMediaSlotUiState(invalidSlot.displayOrder, {
          error: {
            feedbackType: 'error',
            helperText: errorMessage,
          },
        })

        dispatch(
          setToastNotifications({
            show: true,
            message: errorMessage,
            type: 'error',
          })
        )

        return
      }

      const firstSlot = reorderedSlots.find(slot => slot.displayOrder === 1)
      const hasImageInFirstSlotAfterReorder = firstSlot?.mediaKind === 'IMAGE'
      const hasVideoOrGifInLaterSlots = reorderedSlots.some(
        slot => slot.displayOrder !== 1 && (slot.mediaKind === 'MP4' || slot.mediaKind === 'GIF')
      )

      if (!hasImageInFirstSlotAfterReorder && hasVideoOrGifInLaterSlots) {
        const errorMessage = 'You must keep an image in slot 1 before placing a video or GIF in slots 2 or 3.'

        updateMediaSlotUiState(1, {
          error: {
            feedbackType: 'error',
            helperText: errorMessage,
          },
        })

        dispatch(
          setToastNotifications({
            show: true,
            message: errorMessage,
            type: 'error',
          })
        )

        return
      }

      displayOrderMap.forEach((newOrder, oldOrder) => {
        const media = mediaByDisplayOrder.get(oldOrder) || {file: null, imageUri: '', metadata: null}
        updateMediaSlot(newOrder, remapMediaForDisplayOrder(media, newOrder))
      })

      setMediaSlotUiState(prev => {
        const remapped = remapByDisplayOrder(prev, displayOrderMap)
        const cleared = {}

        Object.entries(remapped).forEach(([displayOrder, value]) => {
          cleared[displayOrder] = {
            ...value,
            error: initialFileErrorState,
          }
        })

        return cleared
      })
    },
    [dispatch, mediaSlots, updateMediaSlot, updateMediaSlotUiState]
  )

  return {
    mediaSlots: galleryMediaSlots,
    isDeletingFromServer,
    handleUpload,
    handleUploadError,
    handleDeleteImage,
    handleDeleteFromServer,
    handleReorder,
    mediaSlotUiState
  }
}
